import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import AddAccountDialog from './AddAccountDialog';
import './Accounts.css';

function Accounts({ onViewTransactions }) {
  const [accounts, setAccounts] = useState([]);
  const [balances, setBalances] = useState({});
  const [groupBy, setGroupBy] = useState(false); // false = flat list, true = grouped
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const user = auth.currentUser;

  // Load balances
  useEffect(() => {
    if (!user) return;
    const balancesRef = doc(db, 'users', user.uid, 'balances', 'balances');
    const unsubscribe = onSnapshot(balancesRef, (snapshot) => {
      if (snapshot.exists()) {
        setBalances(snapshot.data());
      }
    });
    return unsubscribe;
  }, [user]);

  // Load accounts
  useEffect(() => {
    if (!user) return;
    const accountsRef = collection(db, 'users', user.uid, 'account_setup');
    const unsubscribe = onSnapshot(accountsRef, (snapshot) => {
      const accountsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort accounts by the last 4 characters of Ledger code (numerically)
      accountsData.sort((a, b) => {
        const getLedgerNumber = (ledger) => {
          if (!ledger) return 0;
          const lastFour = ledger.slice(-4);
          return parseInt(lastFour, 10) || 0;
        };
        return getLedgerNumber(a.Ledger) - getLedgerNumber(b.Ledger);
      });
      
      setAccounts(accountsData);
    });
    return unsubscribe;
  }, [user]);

  const formatBalance = (balance) => {
    return balance.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Group accounts by group
  const groupedAccounts = accounts.reduce((acc, account) => {
    const group = account.group || 'Ungrouped';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(account);
    return acc;
  }, {});

  // Calculate group totals
  const getGroupTotal = (accountsInGroup) => {
    return accountsInGroup.reduce((sum, account) => {
      return sum + (balances[account.name] || 0);
    }, 0);
  };

  const renderAccountRow = (account) => {
    const balance = balances[account.name] || 0;
    
    const handleEdit = () => {
      setEditingAccount(account);
      setShowAddDialog(true);
    };

    const handleViewTransactions = () => {
      if (onViewTransactions) {
        onViewTransactions(account);
      }
    };
    
    return (
      <tr key={account.id}>
        <td className="account-name">
          <strong>{account.name}</strong>
        </td>
        <td className="account-ledger">
          {account.Ledger || '-'}
        </td>
        <td className="account-group">
          {account.group || '-'}
        </td>
        <td className={`account-balance ${balance >= 0 ? 'positive' : 'negative'}`}>
          £{formatBalance(balance)}
        </td>
        <td className="account-status">
          <span className={`status-badge ${account.active ? 'active' : 'inactive'}`}>
            {account.active ? 'Yes' : 'No'}
          </span>
        </td>
        <td className="account-flag">
          {account.allowIncome ? '✅' : '—'}
        </td>
        <td className="account-flag">
          {account.allowExpense ? '✅' : '—'}
        </td>
        <td className="account-flag">
          {account.allowTransfer ? '✅' : '—'}
        </td>
        <td className="account-actions">
          <button className="btn-records" onClick={handleViewTransactions}>Records</button>
          <button className="btn-edit" onClick={handleEdit}>Edit</button>
          <button className="btn-delete-disabled" disabled>Delete</button>
        </td>
      </tr>
    );
  };

  return (
    <div className="section-content">
      <div className="section-header">
        <h2>Accounts</h2>
        <div className="header-actions">
          <button 
            className={`btn-toggle ${groupBy ? 'active' : ''}`}
            onClick={() => setGroupBy(!groupBy)}
          >
            {groupBy ? '📋 Grouped View' : '📋 Group by Category'}
          </button>
          <button className="btn-primary" onClick={() => setShowAddDialog(true)}>+ Add Account</button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <p className="empty-state">No accounts yet.</p>
      ) : groupBy ? (
        // Grouped view
        <div className="grouped-accounts">
          {Object.keys(groupedAccounts).sort().map(group => {
            const groupAccounts = groupedAccounts[group];
            const groupTotal = getGroupTotal(groupAccounts);
            
            return (
              <div key={group} className="account-group-section">
                <div className="group-header">
                  <h3>{group}</h3>
                  <div className={`group-total ${groupTotal >= 0 ? 'positive' : 'negative'}`}>
                    Total: £{formatBalance(groupTotal)}
                  </div>
                </div>
                
                <table className="accounts-table">
                  <thead>
                    <tr>
                      <th>NAME</th>
                      <th>LEDGER</th>
                      <th>GROUP</th>
                      <th className="right">BALANCE</th>
                      <th className="center">ACTIVE</th>
                      <th className="center">INCOME</th>
                      <th className="center">EXPENSE</th>
                      <th className="center">TRANSFER</th>
                      <th className="center">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupAccounts.map(renderAccountRow)}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      ) : (
        // Flat view
        <table className="accounts-table">
          <thead>
            <tr>
              <th>NAME</th>
              <th>LEDGER</th>
              <th>GROUP</th>
              <th className="right">BALANCE</th>
              <th className="center">ACTIVE</th>
              <th className="center">INCOME</th>
              <th className="center">EXPENSE</th>
              <th className="center">TRANSFER</th>
              <th className="center">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(renderAccountRow)}
          </tbody>
        </table>
      )}

      <AddAccountDialog
        isOpen={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setEditingAccount(null);
        }}
        onSuccess={() => {
          // Accounts will auto-reload via onSnapshot
        }}
        editAccount={editingAccount}
      />
    </div>
  );
}

export default Accounts;